import { useForm } from 'react-hook-form';
import {
  LabelVersionDetailsFormComponentProps,
  LabelVersionDetailsFormData,
} from 'app/components/Releases/LabelVersionDetailsForm/types';
import { convertSongVersionFormToApiRequestData } from 'app/components/Releases/LabelVersionDetailsForm/utils';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { ddlToOptions, getValidDirtyValues } from 'app/utils/utils';
import { useEffect, useMemo } from 'react';
import { ReleaseVersion, UpdateVersionSongValidations } from 'types/releases';
import { DDL } from 'types/general.types';
import { SongDDL } from 'types/song';
import * as yup from 'yup';
import { stringValidation } from 'app/utils/validations';
import {
  durationValidation,
  getOptions,
  numberValidation,
} from 'app/components/Song/Details/SongDetailsForm/utils';
import {
  getLanguage,
  getReleaseSongVersionProperty,
} from 'app/components/Releases/utils';

export const getKeySignature = (songDDL: SongDDL, keySignature?: string) =>
  keySignature
    ? {
        title: songDDL.keySignatures[keySignature].name,
        value: songDDL.keySignatures[keySignature].key,
      }
    : null;

const getDefaultValues = (
  song: ReleaseVersion['song'],
  songDDL: SongDDL,
  languageCodes: DDL
): LabelVersionDetailsFormData => {
  const timeSignature = getReleaseSongVersionProperty(song, 'timeSignature');
  return {
    altTitle: getReleaseSongVersionProperty(song, 'altTitle'),
    displayArtistName: getReleaseSongVersionProperty(song, 'displayArtistName'),
    duration: getReleaseSongVersionProperty(song, 'duration'),
    timeSignature: timeSignature
      ? { title: timeSignature, value: timeSignature }
      : null,
    keySignature: getKeySignature(
      songDDL,
      getReleaseSongVersionProperty(song, 'keySignature')
    ),
    tempo: getReleaseSongVersionProperty(song, 'tempo'),
    explicit: song.overrides.explicit ?? (song.snapshot.explicit || false),
    language: getLanguage(
      languageCodes,
      getReleaseSongVersionProperty(song, 'language')
    ),
    location: getReleaseSongVersionProperty(song, 'location.address'), // TODO check getter
  };
};

const getUpdateVersionValidationSchema = (
  validations: UpdateVersionSongValidations
) => {
  const {
    altTitle,
    displayArtistName,
    tempo,
    duration,
    location,
  } = validations;

  return yup.object().shape({
    altTitle: stringValidation(altTitle.maxLength),
    displayArtistName: stringValidation(displayArtistName.maxLength),
    tempo: numberValidation(tempo.minimum, tempo.maximum),
    duration: durationValidation(duration.minimum, duration.maximum),
    location: stringValidation(location.properties.address.maxLength),
  });
};

export const useLabelVersionForm = ({
  songDDL,
  releaseVersion,
  updateSongVersion,
  isSyncing,
  languageCodes,
  validations,
}: LabelVersionDetailsFormComponentProps) => {
  const { song } = releaseVersion;

  const useFormProps = useForm<LabelVersionDetailsFormData>({
    defaultValues: getDefaultValues(song, songDDL, languageCodes),
    mode: 'all',
    shouldFocusError: false,
    resolver: yupResolver(getUpdateVersionValidationSchema(validations)),
  });

  const { handleSubmit, formState, reset, getValues } = useFormProps;

  const { dirtyFields, errors } = formState;
  const disableAllFields = isSyncing;

  const submitHandler = async () => {
    const data = getValues();
    const validDirtyValues = getValidDirtyValues<LabelVersionDetailsFormData>(
      errors,
      dirtyFields,
      data
    );

    if (Object.keys(validDirtyValues).length) {
      reset(data, { keepErrors: true });
      await updateSongVersion(
        convertSongVersionFormToApiRequestData(
          validDirtyValues,
          releaseVersion,
          songDDL.genres
        )
      );
    }
  };

  const onSubmit = handleSubmit(submitHandler, submitHandler);

  useEffect(() => {
    reset(getDefaultValues(song, songDDL, languageCodes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song.snapshotUpdated.timestamp]);

  return {
    onSubmit,
    disableAllFields,
    ...useFormProps,
  };
};

interface UseGenresProps {
  songDDL: SongDDL;
  song: ReleaseVersion['song'];
}

export const useGenres = ({ songDDL, song }: UseGenresProps) => {
  const genres = useMemo(() => ddlToOptions(songDDL.genres, true), [
    songDDL.genres,
  ]);

  const selectedGenres = useMemo(
    () =>
      getOptions(
        { enum: song.overrides.genres || song.snapshot.genres || [] },
        songDDL.genres,
        true
      ),
    [song.overrides.genres, song.snapshot.genres, songDDL.genres]
  );

  return { genres, selectedGenres };
};
